<template>
  <!-- Dashboard Content ================================================== -->
  <div class="dashboard-content-container" data-simplebar>
    <div class="dashboard-content-inner">
      <!-- Dashboard Headline -->
      <div class="dashboard-headline">
        <h3>All Users</h3>

        <!-- Breadcrumbs -->
        <nav id="breadcrumbs" class="dark">
          <ul>
            <li><a href="#">Home</a></li>
            <li><a href="#">Dashboard</a></li>
            <li>All Users</li>
          </ul>
        </nav>
      </div>

      <!-- Row -->
      <div class="row">
        <!-- Dashboard Box -->
        <div class="col-xl-12">
          <div class="dashboard-box margin-top-0">
            <!-- Headline -->
            <div class="headline">
              <h3>
                <i class="icon-material-outline-supervisor-account"></i>
                {{ totalUsers }} Users
              </h3>
              <input type="text"  id="search-box" v-model="searchText" placeholder="Search for a user...">
            </div>

            <div class="content container padding-bottom-60 padding-top-20">
              <ve-table
                :columns="columns"
                :table-data="tableData"
                :border-around="true"
                :border-x="true"
                :border-y="true"
                :fixed-header="true"
              ></ve-table>
              <div class="table-pagination">
                <ve-pagination
                  :total="totalUsers"
                  :page-index="pageIndex"
                  :page-size="pageSize"
                  @on-page-number-change="pageNumberChange"
                  @on-page-size-change="pageSizeChange"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Delete user modal -->
      <admin-modal
        class="modal-delete"
        modalTitle="Delete User"
        v-show="confirmDeleteModal"
        @close-modal="closeModal()"
      >
        <h1 class="header">Are you sure you want to delete user?</h1>
        <div class="cta">
          <button class="secondary-cta" @click="selectUserToDelete(false)">
            No, cancel
          </button>
          <button class="primary-cta" @click="deleteRow(selectedUserId)">
            Yes, delete
          </button>
        </div>
      </admin-modal>

      <!-- View user modal -->
      <div v-if="allUsers">
        <admin-modal
          class="modal-view"
          modalTitle="View user"
          v-if="viewUserModal"
          @close-modal="closeModal()"
        >
          <div class="personal">
            <div class="personal-img">
              <img
                :src="allUsers[selectedUserRowId].avatar"
                alt="user profile picture"
                class="profile-picture"
              />
            </div>
            <div class="personal-details">
              <h1 class="name">{{ allUsers[selectedUserRowId].name }}</h1>
              <h4 class="job-title">{{ allUsers[selectedUserRowId].title }}</h4>
              <div class="meta-info">
                <div
                  class="star-rating"
                  :data-rating="allUsers[selectedUserRowId].ratings + '.0'"
                >
                  <span class="star"></span>
                  <span class="star"></span>
                  <span class="star"></span>
                  <span class="star"></span>
                  <span class="star"></span>
                </div>
                <div class="country">
                  <img src="" class="flag" alt="user country" />
                  <span>Germany</span>
                </div>
                <div
                  class="verified-badge-with-title"
                  v-if="allUsers[selectedUserRowId].account_secured"
                >
                  Verified
                </div>
              </div>
            </div>
          </div>

          <div class="grid-container">
            <div class="user-bio">
              <h2>About the user</h2>
              <p>
                {{
                  allUsers[selectedUserRowId].bio
                    ? allUsers[selectedUserRowId].bio
                    : "null"
                }}
              </p>
            </div>

            <div class="contact">
              <h2>Contact details</h2>
              <ul class="contact-details">
                <li>
                  <i class="bx bxs-map"></i>
                  <span>{{
                    allUsers[selectedUserRowId].address
                      ? allUsers[selectedUserRowId].address
                      : "null"
                  }}</span>
                </li>
                <li>
                  <i class="bx bxs-phone"></i>
                  <a
                    :href="'tel:' + allUsers[selectedUserRowId].phone_number"
                    >{{
                      allUsers[selectedUserRowId].phone_number
                        ? allUsers[selectedUserRowId].phone_number
                        : "null"
                    }}</a
                  >
                </li>
                <li>
                  <i class="bx bxs-envelope"></i>
                  <a :href="'mailto:' + allUsers[selectedUserRowId].email">{{
                    allUsers[selectedUserRowId].email
                      ? allUsers[selectedUserRowId].email
                      : "null"
                  }}</a>
                </li>
                <li>
                  <i class="bx bxl-linkedin"></i>
                  <a :href="allUsers[selectedUserRowId].linkedln">{{
                    allUsers[selectedUserRowId].linkedln
                      ? allUsers[selectedUserRowId].linkedln
                      : "null"
                  }}</a>
                </li>
              </ul>
            </div>
          </div>
        </admin-modal>
      </div>

      <!-- Edit user modal -->
      <admin-modal
        class="modal-edit"
        modalTitle="Edit User"
        v-show="editUserModal"
        @close-modal="closeModal()"
      >
        <form>
          <div class="group">
            <div class="input-box">
              <label for="name">Name</label>
              <input type="text" id="name" v-model="editUserDetails.name" />
            </div>
            <div class="input-box">
              <label for="title">Title</label>
              <input type="text" id="title" v-model="editUserDetails.title" />
            </div>
          </div>
          <div class="group">
            <div class="input-box">
              <label for="phone">Phone number</label>
              <input
                type="text"
                id="phone"
                v-model="editUserDetails.phone_number"
              />
            </div>
            <div class="input-box">
              <label for="email">Email</label>
              <input type="text" id="email" v-model="editUserDetails.email" />
            </div>
          </div>
          <div class="group">
            <div class="input-box">
              <label for="country">Country</label>
              <select id="country" v-model="editUserDetails.country_id">
                <option value="" disabled>Select a country...</option>
                <option
                  v-for="country in allCountries"
                  :key="country.id"
                  :value="country.id"
                  >{{ country.name }}</option
                >
              </select>
            </div>
            <div class="input-box">
              <label for="region">Region</label>
              <select id="region" v-model="editUserDetails.region_id">
                <option disabled>Select a region...</option>
                <option v-for="region in userCountryRegions" :key="region.id" :value="region.id">
                  {{region.name}}
                </option>
              </select>
            </div>
          </div>
          <div class="input-box">
            <label for="bio">Bio</label>
            <textarea id="bio" v-model="editUserDetails.bio"></textarea>
          </div>
          <div class="input-box">
            <label for="address">Address</label>
            <textarea id="address" v-model="editUserDetails.address"></textarea>
          </div>
          <div class="input-box">
            <label for="link">LinkedIn</label>
            <input type="text" id="link" v-model="editUserDetails.linkedln" />
          </div>

          <div class="group">
            <div class="input-box">
              <label for="apply">Can apply</label>
              <div class="group">
                <div class="radio-group">
                  <input
                    type="radio"
                    name="apply"
                    id="yes-apply"
                    value="true"
                    v-model="editUserDetails.can_apply"
                  />
                  <label for="yes-apply">Yes</label>
                </div>
                <div class="radio-group">
                  <input
                    type="radio"
                    name="apply"
                    id="no-apply"
                    value="false"
                    v-model="editUserDetails.can_apply"
                  />
                  <label for="no-apply">No</label>
                </div>
              </div>
            </div>
          </div>

          <button type="submit" class="update-user">Update User</button>

        
        </form>
      </admin-modal>

      <app-footer></app-footer>

      <!-- Removed Update user button -->
                      <!-- <button
                  class="button text-white bg-primary"
                  style="color:#0d6efd;"
                  on-click={() => this.editRow(true, rowIndex)}
                >
                  <i class="icon-feather-edit"></i>
                </button> -->
    </div>
  </div>
  <!-- Dashboard Content / End -->
</template>

<script>
/* eslint-disable no-console */
import { AppFooter } from "../../../partials/user-dashboard";
import {
  ADMIN_GET_ALL_USERS,
  ADMIN_GET_USER_PROJECTS,
  ADMIN_DELETE_SELECTED_USER,
} from "../../../../core/config/endpoints/admin";
import {
  COUNTRIES,
  REGIONS_IN_COUNTRY
} from "../../../../core/config/endpoints/location";
import { getCall, deleteCall } from "../../../../plugins/apiCall";
import Snackbar from "node-snackbar";
import AdminModal from "../../../ui/adminUi/AdminModal.vue";

export default {
  name: "AdminAllUsers",
  components: {
    AppFooter,
    AdminModal,
  },
  data() {
    return {
      searchText: '',
      confirmDeleteModal: false,
      viewUserModal: false,
      editUserModal: false,
      selectedUserToView: null,
      selectedUserId: null,
      selectedUserRowId: null,
      allUsers: [],
      allCountries: [],
      userCountryRegions: [],
      editUserDetails: {
        name: "",
        title: "",
        phone_number: "",
        address: "",
        email: "",
        bio: "",
        can_apply: "",
        linkedln: "",
        country_id: "",
        region_id:'',
      },
      totalUsers: 0,
      // page index
      pageIndex: 1,
      // page size
      pageSize: 10,
      fixedHeader: true,
      columns: [
        {
          field: "",
          key: "#",
          title: "#",
          align: "center",
          renderBodyCell: ({ rowIndex }) => {
            // console.log((this.pageIndex - 1) * this.pageSize + rowIndex + 1);
            return (this.pageIndex - 1) * this.pageSize + rowIndex + 1;
          },
        },
        {
          field: "name",
          key: "name",
          title: "Name",
          align: "center",
        },
        {
          field: "title",
          key: "title",
          title: "Title",
          align: "left",
        },
        {
          field: "",
          key: "action",
          title: "Action",
          width: "",
          center: "left",
          renderBodyCell: ({ rowIndex }) => {
            return (
              <span>
                <button
                  class="button text-white bg-success"
                  style="color:#198754; "
                  on-click={() => this.viewRow(true, rowIndex)}
                >
                  <i class="icon-feather-eye"></i>
                </button>
                &nbsp;
                &nbsp;
                <button
                  class="button text-white bg-danger"
                  style="color:#dc3545;"
                  on-click={() => this.selectUserToDelete(true, rowIndex)}
                >
                  <i class="icon-feather-trash-2"></i>
      </button>
              </span>
            );
          },
        },
      ],
    };
  },
  computed: {
    // table data
    tableData() {
      const { pageIndex, pageSize } = this;
      return this.allUsers.filter(user =>{
        return user.name.toLowerCase().match(this.searchText.toLowerCase());
      }).slice(
        (pageIndex - 1) * pageSize,
        pageIndex * pageSize
      )
    },
    // total count
    totalCount() {
      return this.allUsers.length;
    },
  },
  methods: {
    closeModal(value) {
      this.confirmDeleteModal = value;
      this.viewUserModal = value;
      this.editUserModal = value;
    },
    // page number change
    pageNumberChange(pageIndex) {
      this.pageIndex = pageIndex;
    },
    // page size change
    pageSizeChange(pageSize) {
      this.pageIndex = 1;
      this.pageSize = pageSize;
    },
    getAllUsers() {
      this.$store.commit("setIsLoaded", true);

      getCall(ADMIN_GET_ALL_USERS)
        .then(response => {
          this.allUsers = response.data.data.data;
          this.totalUsers = this.allUsers.length;
          // console.log(response);
        })
        .catch(error => {
          this.$toast.error(`${error.response.data.message}`);
        })
        .finally(() => {
          this.$store.commit("setIsLoaded", false);
        });
    },

    getUserProjects() {
      this.$store.commit("setIsLoaded", true);
      getCall(ADMIN_GET_USER_PROJECTS)
        .then(response => {
          console.log(response);
        })
        .catch(error => {
          this.$toast.error(`${error.response.data.message}`);
        })
        .finally(() => {
          this.$store.commit("setIsLoaded", false);
        });
    },
    selectUserToDelete(value, rowId) {
      this.confirmDeleteModal = value;
      this.selectedUserId = this.allUsers[rowId].id;
      this.selectedUserRowId = rowId;
    },
    viewRow(value, rowId) {
      

      this.viewUserModal = value;
      if(this.pageIndex > 1){
        rowId += 10
        this.selectedUserId = this.allUsers[rowId].id;
      }
      else{
        this.selectedUserId = this.allUsers[rowId].id;
      }
      this.selectedUserRowId = rowId;
      alert(`view row number:${rowId}`);
    },
    deleteRow(userId) {
      this.totalUsers = this.allUsers.length;

      // this.allUsers.splice(rowIndex, 1);
      deleteCall(ADMIN_DELETE_SELECTED_USER.USER_ID(userId))
        .then(response => {
          if (response.status === 204) {
            this.confirmDeleteModal = false;
            this.allUsers.splice(this.selectedUserRowId, 1);
            Snackbar.show({
              text: "User Deleted!",
              pos: "bottom-center",
              showAction: false,
              actionText: "Dismiss",
              duration: 2000,
              delay: 4000,
              textColor: "#fff",
              backgroundColor: "#383838",
            });
          }
        })
        .catch(error => {
          console.log(error);
          console.log("error occured");
        });
    },
    getCountries() {
      getCall(COUNTRIES)
        .then(response => {
          this.allCountries = response.data.data;
        })
        .catch(error => {
          console.log(error);
        });
    },
    getRegions() {
      let countryId = this.editUserDetails.country_id;

      getCall(REGIONS_IN_COUNTRY + countryId)
        .then(response => {
          this.userCountryRegions = response.data.data;
        })
        .catch(error => {
          console.log(error);
        });
    },
  },
  created() {
     this.getAllUsers();
  },
  mounted() {
    
   this.getCountries();
  },
};
</script>

<style scoped>

.headline{
  display: flex !important;
  align-items: center;
  justify-content: space-between;
}
#search-box{
  display: inline-block;
  width: 35%;
  align-self: flex-end;
  border: 1px solid #bfbfbf;
}
#search-box:focus-visible{
  border: 1px solid #2a41e8;
}
.table-pagination {
  margin-top: 20px;
  text-align: right;
}
.modal-delete .header {
  font-size: 1.5rem;
  font-weight: 700;
  color: #333;
  text-align: center;
}
.modal-delete .cta {
  width: max-content;
  /* border: 1px solid red; */
  margin: 20px auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.modal-delete .cta .secondary-cta {
  font-weight: 700;
  padding: 10px 20px;
}
.modal-delete .cta .secondary-cta:hover {
  color: #333;
}
.modal-delete .cta .primary-cta {
  font-weight: 700;
  background: rgba(255, 0, 0, 0.26);
  padding: 10px 20px;
  color: red;
  border-radius: 5px;
  transition: background 0.25s ease;
}
.modal-delete .cta .primary-cta:hover {
  background: rgba(255, 0, 0, 0.5);
}

.modal-view {
  position: fixed;
}

.modal-view .personal {
  position: relative;
  display: flex;
  align-items: center;
}

.modal-view .personal .profile-picture {
  position: relative;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  display: inline-block;
}
.modal-view .personal .personal-img {
  width: 120px;
  height: 120px;
  position: relative;
  margin-right: 25px;
}
.modal-view .personal .personal-img .verified-badge {
  position: absolute;
  bottom: 5px;
  right: 5px;
}

.modal-view .personal-details {
  position: relative;
}
.modal-view .personal h1 {
  color: #333;
  font-weight: 500;
  font-size: 1.58rem;
}
.modal-view .personal h4 {
  color: #808080;
  font-weight: 500;
  font-size: 1.1rem;
  line-height: 1.4;
}

.modal-view .meta-info {
  margin-top: 20px;
  display: block;
  display: flex;
  align-items: center;
  /* justify-content: space-between; */
}

.modal-view .meta-info .star-rating {
  display: inline-flex;
  align-items: center;
  margin-right: 25px;
}

.modal-view .meta-info .country {
  display: inline-flex;
  margin-right: 25px;
}

.modal-view .meta-info .country img {
  width: 21px;
  height: 16px;
}

.modal-view .grid-container {
  position: relative;
  margin-top: 50px;
}

.modal-view .grid-container h2 {
  margin-bottom: 10px;
}

.modal-view .contact {
  margin-top: 50px;
}

.modal-view .contact h2 {
  margin-bottom: 10px;
}

.modal-view .contact ul {
  display: block;
  list-style: none;
  /* border: 1px solid red; */
}
.modal-view .contact ul li {
  list-style-position: outside;
  line-height: 1.5;
  margin-bottom: 10px;
  margin-left: -40px;
}
.modal-view .contact ul li span {
  vertical-align: middle;
}
.modal-view .contact ul li i {
  vertical-align: middle;
  margin-right: 10px;
  width: 20px;
}
.modal-view .contact ul li a {
  vertical-align: middle;
}

.modal-edit .modal-body {
  position: relative;
}

.modal-edit .modal-body form {
  width: 100%;
  position: relative;
}

.modal-edit form .group {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
}
.modal-edit form .group .input-box {
  position: relative;
  width: 48%;
  max-width: 48%;
  margin-bottom: 15px;
}
.modal-edit form .input-box {
  position: relative;
  margin-bottom: 15px;
}
.modal-edit form .group .input-box input {
  border: 1px solid #bfbfbf;
}
.modal-edit form .group .input-box input:focus {
  border: 1px solid #2a41e8;
}

.modal-edit form .group .input-box select {
  border: 1px solid #bfbfbf;
  padding-top: 5px;
  padding-bottom: 5px;
}

.modal-edit form .group .input-box select:focus {
  border: 1px solid #2a41e8;
}
.modal-edit form label {
  color: #575757;
  font-weight: 600;
  margin-bottom: 5px;
}

.modal-edit form .input-box input {
  border: 1px solid #bfbfbf;
}
.modal-edit form .input-box input:focus {
  border: 1px solid #2a41e8;
}

.modal-edit form .input-box textarea {
  border: 1px solid #bfbfbf;
  resize: none;
}

.modal-edit form .input-box span {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(30%);
  font-size: 1.4rem;
  cursor: pointer;
}

.modal-edit form .input-box span:hover {
  color: #141414;
}

.modal-edit form .group .group {
  justify-content: flex-start;
}
.modal-edit form .group .group .radio-group {
  margin-right: 45px;
  display: flex;
  align-items: center;
}
.modal-edit form .group .group .radio-group label {
  display: inline-block;
  vertical-align: middle;
  margin-bottom: 2px;
}
.modal-edit form .group .group .radio-group input[type="radio"] {
  display: inline-block;
  vertical-align: middle;
  margin-bottom: 2px;
  margin-right: 10px;
}

button.update-user {
  width: 100%;
  max-width: 100%;
  padding: 15px 0;
  background: #2a41e8;
  color: #fff;
  margin-top: 20px;
  border-radius: 4px;
  transition: 0.2s ease;
}
button.update-user:hover {
  background: #1d2da7;
}
</style>
